// v3/config/constants.ts — Re-exported enums and shared constants
//
// All imports from v3/shared/types/domain.ts — zero imports from v1/v2.

export {
  Role,
  ProductType,
  ApplicationState,
  Action,
  TaskStatus,
  TaskType,
} from '../shared/types/domain';

// ─── Document Categories ─────────────────────────────────────────────────────

export const DOCUMENT_CATEGORIES = [
  'APPLICATION',
  'PAN',
  'AADHAR',
  'BANK',
  'PROJECT',
  'INVOICE',
  'RESIDENCE_VERIFICATION_REPORT',
  'SANCTION_LETTER',
  'OTHERS',
  'TASK_ATTACHMENTS',
  'RESIDENCE_VERIFICATION_FORM',
] as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

// ─── Task Priorities ─────────────────────────────────────────────────────────

export const TASK_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;

// ─── Product Type Display Labels ─────────────────────────────────────────────

export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  PERSONAL_LOAN: 'Personal Loan',
  BUSINESS_LOAN: 'Business Loan',
  CONSUMER_DURABLES_LOAN: 'Consumer Durables Loan',
  GROUP_LOAN: 'Group Loan',
};

// ─── Indian States (Serviceable Areas) ───────────────────────────────────────

export const INDIAN_STATES = ['Karnataka', 'Telangana', 'Maharashtra', 'Gujarat', 'Uttar Pradesh', 'Andhra Pradesh', 'Delhi', 'Others'];
export const CITY_MAP: Record<string, string[]> = {
  Karnataka: ['Belgaum', 'Bengaluru', 'Davangere', 'Dharwad', 'Hubli', 'Mysuru', 'Tumkur'],
  Telangana: ['Hyderabad', 'Secunderabad', 'Warangal'],
  Maharashtra: ['Kolhapur', 'Mumbai', 'Nagpur', 'Navi Mumbai', 'Pune', 'Thane'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara'],
  'Uttar Pradesh': ['Ghaziabad', 'Noida'],
  Haryana: ['Faridabad', 'Gurugram'],
  'Andhra Pradesh': ['Visakhapatnam'],
  Delhi: ['Delhi'],
  Others: ['Others'],
};

export const INDIAN_CITIES = Array.from(new Set(Object.values(CITY_MAP).flat()));

// ─── All Indian States (Full Set) ────────────────────────────────────────────

export const ALL_INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

export const ALL_CITY_MAP: Record<string, string[]> = {
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati', 'Nellore', 'Other'],
  'Arunachal Pradesh': ['Itanagar', 'Other'],
  Assam: ['Guwahati', 'Silchar', 'Dibrugarh', 'Other'],
  Bihar: ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur', 'Other'],
  Chhattisgarh: ['Raipur', 'Bhilai', 'Bilaspur', 'Other'],
  Goa: ['Panaji', 'Margao', 'Vasco da Gama', 'Other'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar', 'Other'],
  Haryana: ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Other'],
  'Himachal Pradesh': ['Shimla', 'Manali', 'Dharamshala', 'Other'],
  Jharkhand: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Other'],
  Karnataka: ['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru', 'Belgaum', 'Tumkur', 'Other'],
  Kerala: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Other'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Other'],
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Navi Mumbai', 'Nashik', 'Aurangabad', 'Other'],
  Manipur: ['Imphal', 'Other'],
  Meghalaya: ['Shillong', 'Other'],
  Mizoram: ['Aizawl', 'Other'],
  Nagaland: ['Kohima', 'Dimapur', 'Other'],
  Odisha: ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Other'],
  Punjab: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Chandigarh', 'Other'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Other'],
  Sikkim: ['Gangtok', 'Other'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli', 'Other'],
  Telangana: ['Hyderabad', 'Secunderabad', 'Warangal', 'Karimnagar', 'Other'],
  Tripura: ['Agartala', 'Other'],
  'Uttar Pradesh': ['Lucknow', 'Noida', 'Agra', 'Varanasi', 'Kanpur', 'Ghaziabad', 'Meerut', 'Other'],
  Uttarakhand: ['Dehradun', 'Haridwar', 'Rishikesh', 'Other'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Other'],
  'Andaman and Nicobar Islands': ['Port Blair', 'Other'],
  Chandigarh: ['Chandigarh'],
  'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Other'],
  Delhi: ['New Delhi', 'Delhi', 'Other'],
  'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Other'],
  Ladakh: ['Leh', 'Kargil', 'Other'],
  Lakshadweep: ['Kavaratti', 'Other'],
  Puducherry: ['Puducherry', 'Other'],
};
